<template>
  <v-dialog
    v-model="internalDialog"
    persistent
    :max-width="width"
    :style="dialogStyle"
    content-class="draggable-dialog custom-dialog"
  >
    <v-card ref="cardRef">
      <!-- Toolbar -->
      <v-toolbar dense class="custom-toolbar" @mousedown="startDrag" @mouseup="stopDrag">
        <v-toolbar-title class="custom-title">{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Refresh -->
        <v-btn icon @click="onRefresh" color="primary" :disabled="loading">
          <v-icon v-if="!loading">mdi-refresh</v-icon>
          <v-progress-circular v-else indeterminate size="20" width="2" color="primary" />
        </v-btn>

        <!-- Ricerca -->
        <v-text-field
          ref="searchField"
          v-model="search"
          append-icon="mdi-magnify"
          label="Cerca"
          single-line
          hide-details
          dense
          clearable
          flat
          color="primary"
          style="width:200px"
        />
      </v-toolbar>

      <!-- Tabella -->
      <v-card-text class="table-wrapper">
        <div v-if="loading" class="overlay">
          <img src="@/assets/loading.gif" alt="Caricamento..." class="loading-gif" />
        </div>

        <v-radio-group v-model="selected">
          <v-data-table
            :headers="headers"
            :items="filteredItems"
            item-key="id"
            hide-default-footer
            dense
            class="small-text-table"
            :items-per-page="5000"
            fixed-header
            :height="height"
            @click:row="onRowClick"
          >
            <template v-slot:item.radio="{ item }">
              <v-radio :value="item.id" color="primary" hide-details class="radio-center" />
            </template>

            <template v-slot:item.index="{ index }">
              {{ index + 1 }}
            </template>

            <template v-slot:item.descrizione="{ item }">
              {{ item.descrizione }}
            </template>
          </v-data-table>
        </v-radio-group>
      </v-card-text>

      <!-- Pulsanti -->
      <v-card-actions class="justify-center pb-4">
        <v-img
          alt="Annulla"
          contain
          src="@/assets/btn_cancel3_jobmule.png"
          max-width="30"
          max-height="30"
          style="cursor:pointer;margin-right:10px"
          @click="onCancel"
        />
        <v-img
          alt="Conferma"
          contain
          src="@/assets/btn_confirm3_jobmule.png"
          max-width="30"
          max-height="30"
          style="cursor:pointer"
          @click="onConfirm"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ModalTableSelector",
  props: {
    value: Boolean,
    title: { type: String, default: "Seleziona un elemento" },
    items: { type: Array, default: () => [] },
    selectedId: { type: [Number, String], default: null },
    width: { type: [Number, String], default: 800 },
    height: { type: [Number, String], default: 500 },
    draggable: { type: Boolean, default: false },
  },
  data() {
    return {
      internalDialog: this.value,
      localItems: [],
      selected: null,
      search: "",
      loading: false,
      headers: [
        { text: "", value: "radio", align: "center", width: "40px", sortable: false },
        { text: "N.", value: "index", align: "center", width: "50px", sortable: false },
        { text: "Descrizione", value: "descrizione" },
      ],
      drag: { active: false, offsetX: 0, offsetY: 0, x: 0, y: 0 },
    };
  },
  computed: {
    filteredItems() {
      const term = (this.search || "").toLowerCase().trim();
      return this.localItems.filter(i => i.descrizione.toLowerCase().includes(term));
    },
    dialogStyle() {
      const style = this.draggable
        ? { transform: `translate(${this.drag.x}px, ${this.drag.y}px)` }
        : {};
      // 👉 Aggiungiamo sempre un alto z-index per restare sopra i popup
      return { ...style, zIndex: 5000 };
    },
  },
  watch: {
    value(val) {
      this.internalDialog = val;
    },
    internalDialog(val) {
      this.$emit("input", val);
      this.$emit("update:value", val);
      if (val) {
        this.selected = this.selectedId;
        this.search = "";
        this.$nextTick(() => {
          if (this.$refs.searchField) this.$refs.searchField.focus();
        });
      } else {
        this.drag.x = this.drag.y = 0;
      }
    },
    items: {
      immediate: true,
      handler(val) {
        this.localItems = Array.isArray(val) ? [...val] : [];
      },
    },
  },
  methods: {
    onRowClick(item) {
      this.selected = item.id; // ✅ seleziona il radio cliccando sulla riga
    },
    onCancel() {
      this.internalDialog = false;
      this.selected = null;
      this.search = "";
      this.$emit("cancel");
    },
    onConfirm() {
      const item = this.localItems.find(i => i.id === this.selected) || null;
      this.$emit("confirm", item);
      if (item) this.internalDialog = false;
    },
    async onRefresh() {
      this.loading = true;
      this.$emit("refresh");
      setTimeout(() => {
        this.loading = false;
        this.search = "";
        this.selected = null;
      }, 1500);
    },
    startDrag(e) {
      if (!this.draggable) return;
      const dialog = e.currentTarget.closest(".v-dialog__content");
      if (!dialog) return;
      this.drag.active = true;
      this.drag.offsetX = e.clientX - this.drag.x;
      this.drag.offsetY = e.clientY - this.drag.y;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      if (!this.drag.active) return;
      this.drag.x = e.clientX - this.drag.offsetX;
      this.drag.y = e.clientY - this.drag.offsetY;
    },
    stopDrag() {
      this.drag.active = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.draggable-dialog {
  transition: none !important;
}

.table-wrapper {
  position: relative;
  overflow-x: hidden;
  overflow-y: hidden;
}

.v-data-table__wrapper {
  overflow-x: hidden !important;
}

.custom-toolbar {
  background-color: #cce5ff !important;
  cursor: move;
}

.custom-title {
  color: black !important;
  font-weight: bold !important;
  font-size: 13px !important;
}

.small-text-table /deep/ *,
.small-text-table>>>* {
  font-size: 12px !important;
}

.radio-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.loading-gif {
  width: 60px;
  height: 60px;
}

.v-data-table-header {
  border-bottom: 2px solid #c0c0c0;
  z-index: 5;
  position: sticky;
  top: 0;
  background-color: #cce5ff;
}

.v-data-table-header th {
  color: black !important;
  font-weight: bold !important;
  font-size: 12px !important;
}
</style>

<!-- ⚠️ Importante: questa parte NON deve essere scoped -->
<style>
.custom-dialog {
  z-index: 5000 !important;
}
.v-overlay__scrim {
  z-index: 4999 !important;
}
</style>